/**
 * Drafting a bill, supplier credit, request, order or receiving report.
 * Every line carries its tax at the source: VAT class and the ATC withheld.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UserPlus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Decimal, amount, dec } from '../../lib/money';
import { manilaToday, useAccounts, useAtcs, useBranches, useContacts, useItems, useTaxCodes } from '../../lib/queries';
import { AccountSelect } from '../../components/AccountSelect';
import { Alert, Button, Card, Field, Money, PageHeader, Skeleton, filterClass, inputClass } from '../../components/ui';
import { ContactDialog } from '../sales/ContactsPage';
import { KIND_LABEL, VAT_CLASS_LABEL, type InputVatClass, type PurchaseDetail, type PurchaseKind, type PurchaseSummary } from './types';

interface Line {
  key: number;
  itemId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxCode: string;
  inputVatClass: InputVatClass;
  accountId: string;
  atc: string;
}

let nextKey = 1;
const r2 = (d: Decimal): Decimal => d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

export function PurchaseEditorPage(): JSX.Element {
  const { id, kind: kindParam } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const suppliers = useContacts('supplier');
  const branches = useBranches();
  const items = useItems();
  const taxCodes = useTaxCodes();
  const atcs = useAtcs();
  const accounts = useAccounts();
  const existing = useQuery({ queryKey: ['purchase', id], queryFn: () => api<PurchaseDetail>(`/purchase-documents/${id}`), enabled: Boolean(id) });
  const kind = (existing.data?.kind ?? kindParam?.toUpperCase() ?? 'BILL') as PurchaseKind;

  const [contactId, setContactId] = useState(search.get('contact') ?? '');
  const [branchId, setBranchId] = useState('');
  const [date, setDate] = useState(manilaToday());
  const [dueDate, setDueDate] = useState('');
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
  const [supplierInvoiceDate, setSupplierInvoiceDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [inclusive, setInclusive] = useState(false);
  const [billId, setBillId] = useState(search.get('bill') ?? '');
  const [lines, setLines] = useState<Line[]>([]);
  const [newSupplier, setNewSupplier] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const supplier = suppliers.data?.find((s) => s.id === contactId);
  const vatSupplier = supplier?.vatStatus === 'VAT';
  const blank = (): Line => ({ key: nextKey++, itemId: '', description: '', quantity: '1', unitPrice: '', taxCode: vatSupplier === false ? 'NONVAT' : 'VAT12', inputVatClass: 'SERVICES', accountId: supplier?.defaultExpenseAccountId ?? '', atc: supplier?.defaultEwtAtc ?? '' });

  useEffect(() => {
    if (!id && !lines.length) setLines([blank()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!branchId && branches.data?.length) setBranchId(branches.data.find((b) => b.isHeadOffice)?.id ?? branches.data[0].id);
  }, [branches.data, branchId]);
  useEffect(() => {
    const d = existing.data;
    if (!d) return;
    setContactId(d.contactId);
    setBranchId(d.branchId);
    setDate(d.date);
    setDueDate(d.dueDate ?? '');
    setSupplierInvoiceNo(d.supplierInvoiceNo);
    setSupplierInvoiceDate(d.supplierInvoiceDate ?? '');
    setReference(d.reference);
    setNotes(d.notes);
    setInclusive(d.pricesIncludeVat);
    setBillId(d.bill?.id ?? '');
    setLines(d.lines.map((l) => ({ key: nextKey++, itemId: l.itemId ?? '', description: l.description, quantity: String(Number(l.quantity)), unitPrice: dec(l.unitPrice).toFixed(2), taxCode: l.taxCode, inputVatClass: l.inputVatClass, accountId: l.accountId ?? '', atc: l.atc ?? '' })));
  }, [existing.data]);

  const bills = useQuery({
    queryKey: ['purchases', 'credit-targets', contactId],
    queryFn: () => api<{ data: PurchaseSummary[] }>(`/purchase-documents?kind=BILL&status=POSTED&openOnly=true&contactId=${contactId}&pageSize=200`).then((r) => r.data),
    enabled: kind === 'SUPPLIER_CREDIT' && Boolean(contactId),
  });

  const pickSupplier = (sid: string): void => {
    setContactId(sid);
    const s = suppliers.data?.find((x) => x.id === sid);
    if (!s) return;
    setLines((all) => all.map((l) => ({ ...l, accountId: l.accountId || s.defaultExpenseAccountId || '', atc: l.atc || s.defaultEwtAtc || '', taxCode: s.vatStatus === 'VAT' ? (l.taxCode === 'NONVAT' ? 'VAT12' : l.taxCode) : 'NONVAT' })));
  };
  const update = (key: number, patch: Partial<Line>): void => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const pickItem = (key: number, itemId: string): void => {
    const item = items.data?.find((i) => i.id === itemId);
    update(key, item ? { itemId, description: item.name, unitPrice: dec(item.purchasePrice).toFixed(2), taxCode: item.purchaseTaxCode ?? (vatSupplier ? 'VAT12' : 'NONVAT'), inputVatClass: item.type === 'SERVICE' ? 'SERVICES' : 'GOODS', accountId: item.expenseAccountId ?? supplier?.defaultExpenseAccountId ?? '', atc: item.ewtAtc ?? supplier?.defaultEwtAtc ?? '' } : { itemId: '' });
  };

  const totals = useMemo(() => {
    let net = new Decimal(0), vat = new Decimal(0), ewt = new Decimal(0), gross = new Decimal(0);
    const rows = lines.map((l) => {
      const code = taxCodes.data?.find((t) => t.code === l.taxCode);
      const amt = r2(dec(l.quantity).times(dec(l.unitPrice)));
      const hasVat = code && code.kind === 'VAT' && (code.treatment === 'VATABLE' || code.treatment === 'GOVERNMENT');
      const n = hasVat && inclusive ? r2(amt.div(dec(code!.rate).plus(1))) : amt;
      const v = hasVat ? (inclusive ? amt.minus(n) : r2(n.times(dec(code!.rate)))) : new Decimal(0);
      const atc = atcs.data?.find((a) => a.code === l.atc);
      const w = atc ? r2((vatSupplier ? n : n.plus(v)).times(dec(atc.rate))) : new Decimal(0);
      net = net.plus(n); vat = vat.plus(v); ewt = ewt.plus(w); gross = gross.plus(n).plus(v);
      return { gross: n.plus(v), ewt: w };
    });
    return { rows, net, vat, ewt, gross };
  }, [lines, taxCodes.data, atcs.data, inclusive, vatSupplier]);

  const save = useMutation({
    mutationFn: async (then: 'draft' | 'submit') => {
      const body = {
        kind, branchId, contactId, date, dueDate: dueDate || null, supplierInvoiceNo, supplierInvoiceDate: supplierInvoiceDate || null, reference, notes, pricesIncludeVat: inclusive,
        billId: kind === 'SUPPLIER_CREDIT' ? billId || null : null,
        rowVersion: existing.data?.rowVersion,
        lines: lines.filter((l) => l.description.trim() || l.unitPrice).map((l) => ({ itemId: l.itemId || null, description: l.description, quantity: l.quantity || '0', unitPrice: l.unitPrice || '0', taxCode: l.taxCode, inputVatClass: l.inputVatClass, accountId: l.accountId || null, atc: l.atc || null })),
      };
      let doc = id ? await api<PurchaseDetail>(`/purchase-documents/${id}`, { method: 'PATCH', body }) : await api<PurchaseDetail>('/purchase-documents', { method: 'POST', body });
      let submitError: string | undefined;
      if (then === 'submit') {
        try {
          doc = await api<PurchaseDetail>(`/purchase-documents/${doc.id}/submit`, { method: 'POST' });
        } catch (caught) {
          submitError = caught instanceof ApiError ? caught.message : 'Could not submit.';
        }
      }
      return { doc, submitError };
    },
    onSuccess: ({ doc, submitError }) => {
      void queryClient.invalidateQueries({ queryKey: ['purchases'] });
      void queryClient.invalidateQueries({ queryKey: ['purchase', doc.id] });
      navigate(`/purchases/${doc.id}`, { replace: true, state: { warnings: doc.warnings, error: submitError } });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  if (id && existing.isLoading) return <Skeleton className="h-96 w-full" />;
  if (existing.data && existing.data.status !== 'DRAFT') return <Alert tone="warning">Only drafts can be edited.</Alert>;
  const posting = kind === 'BILL' || kind === 'SUPPLIER_CREDIT';
  const ewtAtcs = (atcs.data ?? []).filter((a) => a.kind === 'EWT' && a.active);

  return (
    <>
      <PageHeader title={`${id ? 'Edit' : 'New'} ${KIND_LABEL[kind].toLowerCase()}`} description={posting ? 'Posting books the expense, input VAT, the EWT you withheld and what you owe the supplier.' : undefined} />
      {error && <div className="mb-4"><Alert tone="critical">{error.message}{error.fieldMessages.length > 1 && <ul className="mt-1 list-disc pl-5">{error.fieldMessages.slice(1, 6).map((m) => <li key={m}>{m}</li>)}</ul>}</Alert></div>}
      <Card className="mb-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="md:col-span-2">
            <Field label="Supplier" required>
              <div className="flex gap-2">
                <select className={inputClass} value={contactId} onChange={(e) => pickSupplier(e.target.value)}>
                  <option value="">Choose a supplier</option>
                  {(suppliers.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.registeredName}{s.vatStatus !== 'VAT' ? ' (non-VAT)' : ''}</option>)}
                </select>
                <Button type="button" variant="secondary" aria-label="New supplier" onClick={() => setNewSupplier(true)}><UserPlus aria-hidden="true" className="h-4 w-4" /></Button>
              </div>
            </Field>
          </div>
          <Field label="Branch">
            <select className={inputClass} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {(branches.data ?? []).filter((b) => b.active).map((b) => <option key={b.id} value={b.id}>{b.code} {b.name}</option>)}
            </select>
          </Field>
          <Field label={posting ? 'Date booked' : 'Date'} required><input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          {posting && (
            <>
              <Field label="Supplier's invoice no." required={kind === 'BILL'} error={error?.fields.supplierInvoiceNo}><input className={inputClass} value={supplierInvoiceNo} onChange={(e) => setSupplierInvoiceNo(e.target.value)} /></Field>
              <Field label="Supplier's invoice date"><input type="date" className={inputClass} value={supplierInvoiceDate} onChange={(e) => setSupplierInvoiceDate(e.target.value)} /></Field>
            </>
          )}
          {kind === 'BILL' && <Field label="Due date" hint="Defaults to the supplier's terms."><input type="date" className={inputClass} value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>}
          {kind === 'SUPPLIER_CREDIT' && (
            <div className="md:col-span-2">
              <Field label="Bill it reduces" required>
                <select className={inputClass} value={billId} onChange={(e) => setBillId(e.target.value)} disabled={!contactId}>
                  <option value="">Choose a bill</option>
                  {(bills.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.number} · {b.supplierInvoiceNo} · balance {amount(b.balanceDue)}</option>)}
                </select>
              </Field>
            </div>
          )}
          <Field label="Reference"><input className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
          {vatSupplier && <label className="flex items-center gap-2 self-end pb-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={inclusive} onChange={(e) => setInclusive(e.target.checked)} /> Prices include VAT</label>}
        </div>
      </Card>

      <Card title="Lines">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="w-40 pb-2 pr-2">Item</th>
                <th className="pb-2 pr-2">Description</th>
                <th className="w-16 pb-2 pr-2 text-right">Qty</th>
                <th className="w-28 pb-2 pr-2 text-right">Unit price</th>
                <th className="w-32 pb-2 pr-2">Tax</th>
                <th className="w-32 pb-2 pr-2">VAT class</th>
                <th className="w-44 pb-2 pr-2">Account</th>
                <th className="w-28 pb-2 pr-2">EWT (ATC)</th>
                <th className="w-24 pb-2 pr-2 text-right">EWT</th>
                <th className="w-28 pb-2 pr-2 text-right">Amount</th>
                <th className="w-10 pb-2" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} className="align-top">
                  <td className="py-1.5 pr-2">
                    <select className={`${filterClass} w-full`} value={l.itemId} onChange={(e) => pickItem(l.key, e.target.value)} aria-label={`Line ${i + 1} item`}>
                      <option value="">—</option>
                      {(items.data ?? []).filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{x.code} {x.name}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2"><input className={`${filterClass} w-full`} value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} aria-label={`Line ${i + 1} description`} /></td>
                  <td className="py-1.5 pr-2"><input className={`${filterClass} w-full text-right`} inputMode="decimal" value={l.quantity} onChange={(e) => update(l.key, { quantity: e.target.value })} aria-label={`Line ${i + 1} quantity`} /></td>
                  <td className="py-1.5 pr-2"><input className={`${filterClass} w-full text-right tabular-nums`} inputMode="decimal" value={l.unitPrice} onChange={(e) => update(l.key, { unitPrice: e.target.value.replace(/[^\d.]/g, '') })} aria-label={`Line ${i + 1} unit price`} /></td>
                  <td className="py-1.5 pr-2">
                    <select className={`${filterClass} w-full`} value={l.taxCode} onChange={(e) => update(l.key, { taxCode: e.target.value })} aria-label={`Line ${i + 1} tax`}>
                      {(taxCodes.data ?? []).filter((t) => t.kind !== 'PERCENTAGE').map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2">
                    <select className={`${filterClass} w-full`} value={l.inputVatClass} onChange={(e) => update(l.key, { inputVatClass: e.target.value as InputVatClass })} aria-label={`Line ${i + 1} VAT class`} disabled={l.taxCode !== 'VAT12'}>
                      {Object.entries(VAT_CLASS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2"><AccountSelect accounts={(accounts.data ?? []).filter((a) => a.type === 'EXPENSE' || a.type === 'ASSET')} value={l.accountId} onChange={(accountId) => update(l.key, { accountId })} className={`${filterClass} w-full`} placeholder={posting ? 'Choose' : '—'} /></td>
                  <td className="py-1.5 pr-2">
                    <select className={`${filterClass} w-full`} value={l.atc} onChange={(e) => update(l.key, { atc: e.target.value })} aria-label={`Line ${i + 1} ATC`}>
                      <option value="">None</option>
                      {ewtAtcs.map((a) => <option key={a.code} value={a.code}>{a.code} {(Number(a.rate) * 100).toFixed(0)}%</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2 pt-3 text-right"><Money>{amount(totals.rows[i]?.ewt.toString(), true)}</Money></td>
                  <td className="py-1.5 pr-2 pt-3 text-right"><Money>{amount(totals.rows[i]?.gross.toString())}</Money></td>
                  <td className="py-1.5">
                    <button type="button" className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30" aria-label={`Remove line ${i + 1}`} disabled={lines.length <= 1} onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}><Trash2 aria-hidden="true" className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <Button type="button" size="sm" variant="secondary" onClick={() => setLines((all) => [...all, blank()])}><Plus aria-hidden="true" className="h-4 w-4" /> Add line</Button>
          <dl className="min-w-[16rem] space-y-1 text-sm">
            <div className="flex justify-between gap-6"><dt className="text-slate-500">Net of VAT</dt><dd><Money>{amount(totals.net.toString())}</Money></dd></div>
            <div className="flex justify-between gap-6"><dt className="text-slate-500">VAT</dt><dd><Money>{amount(totals.vat.toString())}</Money></dd></div>
            <div className="flex justify-between gap-6 font-semibold"><dt>Total</dt><dd><Money>{amount(totals.gross.toString())}</Money></dd></div>
            <div className="flex justify-between gap-6"><dt className="text-slate-500">Less EWT you withhold</dt><dd><Money>-{amount(totals.ewt.toString())}</Money></dd></div>
            <div className="flex justify-between gap-6 border-t border-slate-200 pt-1 text-base font-bold dark:border-slate-700"><dt>Net payable</dt><dd><Money>{amount(totals.gross.minus(totals.ewt).toString())}</Money></dd></div>
          </dl>
        </div>
        <div className="mt-4"><Field label="Notes"><textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field></div>
      </Card>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="secondary" busy={save.isPending && save.variables === 'draft'} disabled={!contactId} onClick={() => { setError(null); save.mutate('draft'); }}>Save draft</Button>
        <Button busy={save.isPending && save.variables === 'submit'} disabled={!contactId} onClick={() => { setError(null); save.mutate('submit'); }}>{posting ? 'Save and post' : 'Save and approve'}</Button>
      </div>
      {newSupplier && <ContactDialog role="supplier" contact={null} onClose={() => setNewSupplier(false)} onSaved={(c) => pickSupplier(c.id)} />}
    </>
  );
}
